import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Check, Loader2 } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { Job } from '../../types/backend.types';
import { uploadQueueService } from '../../services/upload-queue.service';
import { openNativeCamera } from '../../utils/camera.utils';
import { isNativeApp, isNativeIOS, isNativeAndroid } from '../../utils/platform.utils';
import { documentScannerService } from '../../services/documentScanner.service';
import { Camera as CapCamera } from '@capacitor/camera';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

interface TimeEntry {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  clock_in_time: string;
}

export const Receipts: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { location: currentLocation, ensureFreshLocation } = useLocation();

  const [loading, setLoading] = useState(true);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [clockedInJob, setClockedInJob] = useState<TimeEntry | null>(null);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [jobSearch, setJobSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [needsReimbursement, setNeedsReimbursement] = useState(false);

  const employeeId = auth?.user?.employee_id;

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      await ensureFreshLocation();

      // Check if clocked in
      const timeResponse = await api.get(API_CONFIG.ENDPOINTS.CURRENT_TIME_ENTRY);
      if (timeResponse.data.success && timeResponse.data.data) {
        const entry = timeResponse.data.data;
        setClockedInJob(entry);
        setCurrentJob({
          job_id: entry.job_id,
          job_name: entry.job_name,
          bradanna_job_number: entry.bradanna_job_number,
          address: '',
          city: '',
          state: '',
          is_active: true
        } as Job);
      }

      // Load all jobs
      const jobsResponse = await api.get(API_CONFIG.ENDPOINTS.JOBS);
      if (jobsResponse.data.success) {
        setAllJobs(jobsResponse.data.data.filter((j: Job) => j.is_active));
      }

      // Get today's receipt count
      await loadTodayCount();
    } catch (err) {
      console.error('[Receipts] Init error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTodayCount = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/api/receipts?employee_id=${employeeId}&start_date=${today}&end_date=${today}`);
      if (response.data.success) {
        setTodayCount(response.data.data.length);
      }
    } catch { /* ignore */ }
  };

  const handleJobSelect = (job: Job) => {
    setCurrentJob(job);
    setShowJobSelector(false);
  };

  // Native document scanner (iOS VisionKit / Android ML Kit)
  const handleNativeScan = async () => {
    if (!currentJob || !employeeId) return;
    setError(null);

    try {
      if (isNativeIOS() && documentScannerService.isAvailable()) {
        // iOS VisionKit document scanner
        const result = await documentScannerService.scanSingle();
        if (result.images && result.images.length > 0) {
          setIsSubmitting(true);
          const resizedBase64 = await documentScannerService.resizeBase64Image(result.images[0], 1200, 0.80);
          const file = documentScannerService.base64ToFile(resizedBase64, `receipt-${Date.now()}.jpg`);

          if (file.size === 0) {
            setError('Photo capture failed. Please try again.');
            setIsSubmitting(false);
            return;
          }

          await uploadQueueService.addToQueue(
            'receipt',
            {
              job_id: currentJob.job_id,
              scanner_type: 'visionkit',
              needs_reimbursement: needsReimbursement ? '1' : '0'
            },
            [{ file, filename: file.name, fieldName: 'receipt_photo', base64: resizedBase64 }],
            employeeId,
            currentJob.job_id
          );

          handleSuccess();
        }
      } else if (isNativeAndroid()) {
        // Android: Use Capacitor Camera
        const photo = await CapCamera.getPhoto({
          quality: 85,
          allowEditing: false,
          resultType: 'base64' as any,
          source: 'CAMERA' as any,
        });

        if (photo.base64String) {
          setIsSubmitting(true);
          const resizedBase64 = await documentScannerService.resizeBase64Image(photo.base64String, 1200, 0.80);
          const file = documentScannerService.base64ToFile(resizedBase64, `receipt-${Date.now()}.jpg`);

          await uploadQueueService.addToQueue(
            'receipt',
            {
              job_id: currentJob.job_id,
              scanner_type: 'camera',
              needs_reimbursement: needsReimbursement ? '1' : '0'
            },
            [{ file, filename: file.name, fieldName: 'receipt_photo', base64: resizedBase64 }],
            employeeId,
            currentJob.job_id
          );

          handleSuccess();
        }
      }
    } catch (err: any) {
      if (err?.message?.includes('cancelled') || err?.message?.includes('User cancelled')) {
        return; // User cancelled, no error
      }
      console.error('[Receipts] Native scan error:', err);
      setError('Failed to scan receipt. Please try again.');
      setIsSubmitting(false);
    }
  };

  // PWA camera fallback
  const handlePWACamera = () => {
    if (!currentJob) return;
    setError(null);

    openNativeCamera(async (fileOrFiles) => {
      const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
      if (!file || !employeeId) return;
      setIsSubmitting(true);

      try {
        await uploadQueueService.addToQueue(
          'receipt',
          {
            job_id: currentJob.job_id,
            scanner_type: 'camera',
              needs_reimbursement: needsReimbursement ? '1' : '0'
          },
          [{ file, filename: file.name, fieldName: 'receipt_photo' }],
          employeeId,
          currentJob.job_id
        );

        handleSuccess();
      } catch (err) {
        console.error('[Receipts] Upload error:', err);
        setError('Failed to upload receipt. Please try again.');
        setIsSubmitting(false);
      }
    });
  };

  const handleScanClick = () => {
    if (!currentJob) {
      setShowJobSelector(true);
      return;
    }

    if (isNativeApp()) {
      handleNativeScan();
    } else {
      handlePWACamera();
    }
  };

  const handleSuccess = () => {
    setIsSubmitting(false);
    setShowSuccess(true);
    setTodayCount(prev => prev + 1);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#f59e0b" />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'white', margin: 0 }}>Receipts</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            {todayCount} scanned today
          </p>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* Job Selection */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 8 }}>
            Job
          </label>
          <button
            onClick={() => setShowJobSelector(true)}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: '2px solid #e5e7eb',
              background: 'white',
              fontSize: 16,
              textAlign: 'left',
              cursor: 'pointer',
              color: currentJob ? '#1e293b' : '#94a3b8'
            }}
          >
            {currentJob ? `${currentJob.job_name}${currentJob.bradanna_job_number ? ` (#${currentJob.bradanna_job_number})` : ''}` : 'Select a job...'}
          </button>
          {clockedInJob && currentJob?.job_id === clockedInJob.job_id && (
            <p style={{ fontSize: 12, color: '#059669', margin: '6px 0 0', fontWeight: 500 }}>
              Currently clocked in
            </p>
          )}
        </div>

        {/* Reimbursement Checkbox */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'white',
          borderRadius: 12,
          padding: 14,
          marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={needsReimbursement}
            onChange={e => setNeedsReimbursement(e.target.checked)}
            style={{ width: 20, height: 20, accentColor: '#f59e0b', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>Needs Reimbursement</span>
        </label>

        {/* Scan Button */}
        <button
          onClick={handleScanClick}
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: '48px 16px',
            borderRadius: 16,
            border: 'none',
            background: isSubmitting ? '#d1d5db' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            fontSize: 18,
            fontWeight: 700,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            boxShadow: isSubmitting ? 'none' : '0 4px 15px rgba(245, 158, 11, 0.3)',
            marginBottom: 16
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
              Uploading...
            </>
          ) : (
            <>
              <Camera size={40} />
              Scan Receipt
            </>
          )}
        </button>

        {/* Error */}
        {error && (
          <div style={{
            padding: 12, borderRadius: 8,
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            color: '#dc2626', fontSize: 14, marginBottom: 16
          }}>
            {error}
          </div>
        )}

        {/* Success Overlay */}
        {showSuccess && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white', borderRadius: 16, padding: 32,
              textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#059669', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Check size={32} color="white" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Receipt Uploaded
              </h3>
              <p style={{ fontSize: 14, color: '#64748b', margin: '8px 0 0' }}>
                AI is processing...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Job Selector Modal */}
      {showJobSelector && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9998, display: 'flex', flexDirection: 'column'
        }}>
          <div style={{
            background: 'white', flex: 1, marginTop: 60,
            borderRadius: '16px 16px 0 0', overflow: 'auto'
          }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Select Job</h3>
              <button onClick={() => setShowJobSelector(false)} style={{ background: 'none', border: 'none', fontSize: 16, color: '#64748b', cursor: 'pointer' }}>Cancel</button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              <input
                type="text"
                value={jobSearch}
                onChange={e => setJobSearch(e.target.value)}
                placeholder="Search jobs..."
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 16, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ padding: '0 16px' }}>
              {allJobs
                .filter(j => !jobSearch || j.job_name.toLowerCase().includes(jobSearch.toLowerCase()) || j.bradanna_job_number?.toLowerCase().includes(jobSearch.toLowerCase()))
                .map(job => (
                  <button
                    key={job.job_id}
                    onClick={() => handleJobSelect(job)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '12px 0',
                      borderBottom: '1px solid #f1f5f9', background: 'none',
                      border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#f1f5f9',
                      cursor: 'pointer', fontSize: 15
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{job.job_name}</div>
                    {job.bradanna_job_number && <div style={{ fontSize: 13, color: '#64748b' }}>#{job.bradanna_job_number}</div>}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Receipts;
